// M6 — Fetch wrapper for labourcore-api (localhost:3010).
// Single-flight 401 refresh, standard error envelope, typed ApiError.

// In dev, Vite proxies /auth, /rpc, /api to localhost:3010.
// In production, VITE_API_BASE_URL points to the real API.
const BASE_URL = (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_API_BASE_URL)
  || "";

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/* ── Token storage ─────────────────────────────────────────────── */

const TOKEN_KEYS = { access: "lc:access_token", refresh: "lc:refresh_token" } as const;

export function getAccessToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEYS.access); } catch { return null; }
}
export function getRefreshToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEYS.refresh); } catch { return null; }
}
export function setTokens(access: string, refresh: string): void {
  try {
    localStorage.setItem(TOKEN_KEYS.access, access);
    localStorage.setItem(TOKEN_KEYS.refresh, refresh);
  } catch { /* SSR / incognito */ }
}
export function clearTokens(): void {
  try {
    localStorage.removeItem(TOKEN_KEYS.access);
    localStorage.removeItem(TOKEN_KEYS.refresh);
  } catch { /* ignore */ }
}

/* ── Parse error from response ─────────────────────────────────── */

async function parseError(res: Response): Promise<ApiError> {
  try {
    const body = await res.json();
    return new ApiError(
      res.status,
      body.code || `HTTP_${res.status}`,
      body.error || body.message || `HTTP ${res.status}`,
      body.details,
    );
  } catch {
    return new ApiError(res.status, `HTTP_${res.status}`, `HTTP ${res.status}`);
  }
}

/* ── Single-flight refresh ─────────────────────────────────────── */

let _refreshPromise: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  const rt = getRefreshToken();
  if (!rt) return false;
  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!res.ok) return false;
    const json = await res.json();
    if (json.access_token && json.refresh_token) {
      setTokens(json.access_token, json.refresh_token);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

async function refreshTokens(): Promise<boolean> {
  if (!_refreshPromise) {
    _refreshPromise = doRefresh().finally(() => { _refreshPromise = null; });
  }
  return _refreshPromise;
}

/* ── Authed fetch with 401 retry ───────────────────────────────── */

export async function authedFetch(url: string, init?: RequestInit): Promise<Response> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    ...(init?.headers as Record<string, string> || {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let res = await fetch(url, { ...init, headers });

  if (res.status === 401) {
    const ok = await refreshTokens();
    if (!ok) {
      clearTokens();
      window.dispatchEvent(new Event("lc:auth-changed"));
      throw new ApiError(401, "SESSION_EXPIRED", "Session expired — please sign in again");
    }
    // Retry with new token
    const newToken = getAccessToken();
    if (newToken) headers["Authorization"] = `Bearer ${newToken}`;
    res = await fetch(url, { ...init, headers });
  }

  return res;
}

/* ── Convenience helpers ───────────────────────────────────────── */

export async function apiPost<T = unknown>(path: string, body?: unknown): Promise<T> {
  const res = await authedFetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as T;
}

export async function apiGet<T = unknown>(path: string, params?: Record<string, string>): Promise<T> {
  let url = `${BASE_URL}${path}`;
  if (params) {
    const sp = new URLSearchParams(params);
    url += `?${sp.toString()}`;
  }
  const res = await authedFetch(url);
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as T;
}

export async function apiPatch<T = unknown>(path: string, body: unknown): Promise<T> {
  const res = await authedFetch(`${BASE_URL}${path}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as T;
}

export { BASE_URL };
